import * as React from 'react';
import { useState, useEffect} from "react";
import { Field, Form } from 'formik';
import * as Yup from 'yup';
import FormikForm from '~/lib/forms/FormikForm';
import SaleHeader from './SaleHeader';
import FooterWrapper from '~/common/elements/mobile/FooterWrapper';
import BodyWrapper from '~/common/elements/mobile/BodyWrapper';
import Button from '~/common/elements/mobile/Button';
import { buttonColor, buttonSize, buttonStyle } from '~/common/elements/mobile/consts';
import { Reg } from '~/apps/pos/mobile/containers/sales/CreateSale';
import PageMobileWrapper from '~/common/elements/mobile/PageMobileWrapper';
import Input from '~/common/controls/Input';
import { fieldType } from '../../containers/sales/CreateSale';
import decimal from '~/lib/normalize/decimal';
import { epnRegExp } from "~/apps/shared/consts";
import { AdditionalField } from '~/apps/pos/models';


type Props = {
  initialValues: Reg,
  onBack: any,
  onSubmit: any,
  onHelp?: any,
  onClose: any,
  setFormData: any,
  additionalFields: AdditionalField[],
  actionKeyBack: number,
}

const SaleStepAdditionalFields = (props: Props) => {
  const {
    initialValues, onHelp, onBack, onClose, onSubmit, setFormData, additionalFields, actionKeyBack,
  } = props;
  const [isInitialValid, setInitialValid] = useState(false);
  const [isSubmit, setSubmit] = useState(false);

  const confirmRequiredInitalValues = additionalFields
    .filter((field) => field.confirmRequired)
    .reduce((prev, curr) => ({ ...prev, [`${curr.id}-confirm`]: null }), {});
  
  // this is required due the issue with not displaying validation errors on "non-touched" inputs
  const extendedInitialValues = { ...initialValues, ...confirmRequiredInitalValues };

  const createSchema = () => {
    const baseSchema: Record<string, any> = additionalFields.reduce((a, i) => {
      if (i.isRequired) {
        switch (i.fieldType) {
          case fieldType.INT:
            return {
              ...a,
              [i.id]: Yup.number().typeError('Must be an integer').integer('Must be integer').required('Required'),
            };
          case fieldType.DECIMAL:
            return {
              ...a,
              [i.id]: decimal().typeError('Must be an decimal').required('Required'),
            };
          case fieldType.EMAIL:
            return {
              ...a,
              [i.id]: Yup.string().typeError('Must be an email').email('Must be an email format').required('Required'),
            };
          case fieldType.STRING:
            return {
              ...a,
              [i.id]: Yup.string().typeError('Required').required('Required'),
            };
          case fieldType.STRING_SIX_DIGIT:
            return {
              ...a,
              [i.id]: Yup.string().typeError('Required').required('Required').matches(epnRegExp, 'Should be exactly 6 digits'),
            };

          default:
            return {
              ...a,
              [i.id]: Yup.string().typeError('Required').required('Required'),
            };
        }
      } else {
        switch (i.fieldType) {
          case fieldType.INT:
            return {
              ...a,
              [i.id]: Yup.number().typeError('Must be an integer').integer('Must be integer'),
            };
          case fieldType.DECIMAL:
            return {
              ...a,
              [i.id]: decimal().typeError('Must be an decimal'),
            };
          case fieldType.EMAIL:
            return {
              ...a,
              [i.id]: Yup.string().typeError('Must be an email').email('Must be an email format'),
            };
          case fieldType.STRING_SIX_DIGIT:
            return {
              ...a,
              [i.id]: Yup.string().nullable().matches(epnRegExp, 'Should be exactly 6 digits'),
            }
          default:
            return a;
        }
      }
    }, {});

    const confirmRequiredSchema = additionalFields
      .filter((field) => field.confirmRequired)
      .map((field) => ({
        [`${field.id}-confirm`]: baseSchema[field.id].clone().oneOf([Yup.ref(field.id)], "Fields must match"),
      }))
      .reduce((prev, curr) => ({ ...prev, ...curr }), {});
  
    return Yup.object().shape({ ...baseSchema, ...confirmRequiredSchema });
  };

  const saleSchema = createSchema();

  const handleHelp = onHelp ? () => { onHelp(actionKeyBack) } : null;

  const handleOnSubmit = (data) => {
    setSubmit(true);
    if (setFormData) setFormData(data);
    return onSubmit(saleSchema.cast(data));
  };

  const renderFields = () => {
    return additionalFields
      .sort((a, b) => (a.priority > b.priority ? 1 : a.priority < b.priority ? -1 : 0))
      .map((field) => (
        <React.Fragment key={field.id}>
          <Field name={field.id} label={field.name} type="text" component={Input} className="input input--sm" />
          {field.confirmRequired && (
            <Field
              name={`${field.id}-confirm`}
              label={`Confirm ${field.name}`}
              type="text"
              component={Input}
              className="input input--sm"
            />
          )}
        </React.Fragment>
      ));
  };

  useEffect(() => {
    saleSchema.isValid(extendedInitialValues).then((valid) => {
      if (valid) setInitialValid(true);
    });
  }, [])

  return (
    <FormikForm
      initialValues={extendedInitialValues}
      onSubmit={handleOnSubmit}
      validationSchema={saleSchema}
      isInitialValid={isInitialValid}
      render={(propsForm) => {
        return (
          <PageMobileWrapper>
            <SaleHeader onBack={onBack} onHelp={handleHelp} onClose={onClose} title="Additional fields" />
            <BodyWrapper>
              <Form className="form__cont">{renderFields()}</Form>
            </BodyWrapper>
            <FooterWrapper>
              <Button
                title={isSubmit ? "Please Wait..." : propsForm.isValid ? "Next" : "Complete fields to continue"}
                buttonStyle={isSubmit ? buttonStyle.SECN : propsForm.isValid ? buttonStyle.OUTL_LITE : buttonStyle.LARY}
                buttonSize={buttonSize.XLG}
                buttonColor={propsForm.isValid ? buttonColor.PRIM : null}
                onClick={!isSubmit ? propsForm.submitForm : null}
              />
            </FooterWrapper>
          </PageMobileWrapper>
        );
      }}
    />
  );
};

export default SaleStepAdditionalFields;
